import { SupportedTpaAppDefIds } from '@wix/a11y-adapters';
import { dsStudioDataModifier } from '@wix/a11y-adapters/studio';
import { assertNotOptional } from '@wix/seo-common-core';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { altTextScreenPageIdAtom } from '../../../atoms/session-atoms';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { useTpaEntityDataHandlers } from '../../../hooks/tpa/use-tpa-entity-data-handlers';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { PanoramaTransactions } from '../../../panorama/constants';
import { usePanoramaFunctions } from '../../../panorama/use-panorama-functions';
import { getCompRefType } from '../../../utils/get-comp-ref-type';
export const useRestaurantsMenuAltTextHandlers = () => {
  const {
    a11yWizardAdapter
  } = useA11yWizardAdapter();
  const {
    setTpaEntityData
  } = useTpaEntityDataHandlers();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const altTextScreenPageId = useAtomValue(altTextScreenPageIdAtom);
  assertNotOptional(altTextScreenPageId);
  const {
    getPagesData
  } = usePanelStateAtom();
  const pageIdToPageData = getPagesData();
  const {
    pageId
  } = altTextScreenPageId;
  const {
    startPanoramaTransaction,
    finishPanoramaTransaction
  } = usePanoramaFunctions();
  const innerRoutes = useMemo(() => {
    var _pageIdToPageData$get;
    return (_pageIdToPageData$get = pageIdToPageData.get(pageId)) == null ? void 0 : _pageIdToPageData$get.innerRoutes;
  }, [pageIdToPageData, pageId]);
  const openImageElement = useCallback(nextElement => {
    const {
      id,
      breakpointId,
      tpaData
    } = nextElement;
    const nextCompRef = {
      id: (tpaData == null ? void 0 : tpaData.componentId) ?? id,
      type: getCompRefType(editorHost, breakpointId)
    };
    if (nextCompRef) {
      a11yWizardAdapter.selectComponent(nextCompRef, breakpointId);
    }
  }, [a11yWizardAdapter, editorHost]);
  const getEntityName = useCallback(entityId => {
    var _innerRoutes$find;
    return (innerRoutes == null || (_innerRoutes$find = innerRoutes.find(innerRoute => innerRoute.id === entityId)) == null ? void 0 : _innerRoutes$find.name) ?? '';
  }, [innerRoutes]);
  const enabledDecorativeImages = true;
  const setAltText = useCallback(async data => {
    const {
      isDecorative,
      alt,
      id,
      tpaData,
      breakpointId
    } = data;
    if (tpaData && tpaData.appDefId) {
      await setTpaEntityData(tpaData.appDefId, [data], true);
      return;
    }
    const compRef = {
      id,
      type: getCompRefType(editorHost, breakpointId)
    };
    const component = a11yWizardAdapter.getComponent(compRef);
    const updatedComponent = dsStudioDataModifier.updateImageProperties(component, {
      alt: isDecorative ? '' : alt,
      decorative: isDecorative ?? false
    });
    updatedComponent.design && a11yWizardAdapter.setComponentDesign(compRef, updatedComponent.design);
    updatedComponent.data && a11yWizardAdapter.setComponentData(compRef, updatedComponent.data);
  }, [a11yWizardAdapter, editorHost, setTpaEntityData]);
  const setBulkAltText = useCallback(async altTextData => {
    startPanoramaTransaction(PanoramaTransactions.bulkApplyStaticPageAltImage, {
      altTextData
    });
    const itemsWithoutTpaData = altTextData.filter(item => !item.tpaData);
    const itemsWithTpaData = altTextData.filter(item => item.tpaData);
    const itemsWithTpaDataByAppDefId = _.groupBy(itemsWithTpaData, _ref => {
      let {
        tpaData
      } = _ref;
      return (tpaData == null ? void 0 : tpaData.appDefId) ?? SupportedTpaAppDefIds.PRO_GALLERY;
    });
    itemsWithoutTpaData.forEach(data => setAltText(data));
    await Promise.all(Object.entries(itemsWithTpaDataByAppDefId).map(async _ref2 => {
      let [appDefId, items] = _ref2;
      await setTpaEntityData(appDefId, items, true);
    }));
    finishPanoramaTransaction(PanoramaTransactions.bulkApplyStaticPageAltImage, {
      altTextData
    });
  }, [finishPanoramaTransaction, setAltText, setTpaEntityData, startPanoramaTransaction]);
  return useMemo(() => ({
    enabledDecorativeImages,
    getEntityName,
    openImageElement,
    setBulkAltText
  }), [enabledDecorativeImages, getEntityName, openImageElement, setBulkAltText]);
};
//# sourceMappingURL=use-restaurants-menu-alt-text-handlers.js.map